/* Free-gift coupon lines, decided in the browser.

   The cart carries a ₹0 coupon product once its subtotal crosses a threshold,
   and loses it again when the subtotal drops back. The rule used to live
   server-side: every add-to-cart asked an app-proxy endpoint which coupon
   applied, and the answer arrived before the theme was allowed to open the
   drawer. The rule is two thresholds, so it lives here and the drawer never
   waits.

   Deliberately Frido-specific — the thresholds and coupon variant ids below are
   this store's, not a platform construct.

   Two properties hold the design together:

   Every decision is recomputed from a whole cart, never from a memory of what
   this file did last. A missed event, a duplicated event, a cart changed in
   another tab, and a page loaded fresh all converge on the same answer.

   The cart it decides from is preferably the one a mutation just returned, so
   the coupon follows the shopper's action as closely as one cart write allows.
   A prediction is always followed by a pass against the authoritative cart, so
   a wrong guess corrects itself rather than persisting. */

(function () {
  if (window.__fridoFreeGift) return;
  window.__fridoFreeGift = true;

  var COUPON_500 = 51535991144729;
  var COUPON_1000 = 51561642230041;

  /* Marks a line as ours, so a pass can remove it, leave a shopper-added line of
     the same variant alone, and exclude it from the subtotal it is judged on. */
  var AUTO_ADDED = '_spectrum_auto_added';

  /* Picks the cart's free-gift row chassis — no stepper, no remove control, no
     PDP link, price reads FREE. Declared rather than left to the cart's $0
     fallback, so a coupon that ever prices above zero still renders as a gift. */
  var LINE_TYPE = '_spectrum_line_type';

  /* The Spectrum SDK stamps this only while its cart-actions pipeline is
     enabled, and disabling that pipeline is what removes the blocking request.
     The order webhook reads it to link an order back to its quote-time points
     hold, so the write moves here. */
  var TOKEN_ATTR = 'SPECTRUM_CART_TOKEN';

  var CART_MUTATION = /\/cart\/(?:add|update|change|clear)(?:\.js)?(?:[/?#]|$)/;
  var CART_ADD = /\/cart\/add(?:\.js)?(?:[/?#]|$)/;

  /* The mutations that can carry a rendered `sections` payload the caller
     repaints from. `add` is excluded: it answers with the added lines rather
     than the whole cart, so it is not a response this file can stand in for. */
  var CART_LINE_CHANGE = /\/cart\/(?:update|change|clear)(?:\.js)?(?:[/?#]|$)/;

  /* Longest the drawer's repaint may be held while the coupon change lands.
     Past it the original response is handed over untouched — a coupon row that
     lingers until the next reconcile is a smaller failure than a cart that
     never repaints at all. */
  var HOLD_MS = 4000;

  /* Collapses the burst one shopper action produces — the repaint, and the
     coupon change it cascades into. Only the fallback listeners debounce; a
     mutation this file observed directly is acted on at once. */
  var SETTLE_MS = 120;

  /* Captured before the wrapper below replaces it. Every request this file makes
     goes through it, so our own writes never re-enter our own handler. */
  var passthrough = window.fetch.bind(window);

  var running = false;
  var again = false;
  var timer = null;

  /* The most recent whole cart seen, from a read or from a mutation response.
     Only ever an optimisation: it seeds a prediction, and a prediction is always
     verified. Nothing is decided from it that a later pass cannot overturn. */
  var lastCart = null;

  function noop() {}

  function remember(cart) {
    if (cart && cart.items) lastCart = cart;
    return cart;
  }

  // --- the rule ---------------------------------------------------------

  function isOurs(item) {
    return item.properties && item.properties[AUTO_ADDED] === 'true';
  }

  function isCoupon(item) {
    return item.variant_id === COUPON_500 || item.variant_id === COUPON_1000;
  }

  /* Subtotal the thresholds are judged on: shopper-added lines only, in rupees.
     Shopify money fields are in minor units. */
  function subtotal(cart) {
    var minor = 0;
    for (var i = 0; i < cart.items.length; i++) {
      if (!isOurs(cart.items[i])) minor += cart.items[i].line_price;
    }
    return minor / 100;
  }

  /* Campaigns that must not receive a coupon. */
  function excluded() {
    var params = new URLSearchParams(window.location.search);
    return (
      params.get('utm_source') === 'cred' ||
      params.get('utm_medium') === 'app' ||
      params.get('utm_campaign') === 'cred-only-friday-december-2025'
    );
  }

  function couponFor(cart) {
    if (excluded()) return null;
    var value = subtotal(cart);
    if (value >= 4999) return COUPON_1000;
    if (value >= 1499) return COUPON_500;
    return null;
  }

  function holdsAny(cart, variantId) {
    for (var i = 0; i < cart.items.length; i++) {
      if (cart.items[i].variant_id === variantId) return true;
    }
    return false;
  }

  /* Line quantities to force, keyed by line key, so the cart ends up holding
     exactly one line of the wanted coupon and nothing of the other.
     Only lines we added: a coupon the shopper put there themselves is theirs.
     Quantity is pinned as well as presence — Shopify merges a repeat add into
     the existing line, so "already present" is not the same as "present once". */
  function lineFixes(cart, wanted) {
    var fixes = {};
    var keptWanted = false;

    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      if (!isOurs(item) || !isCoupon(item)) continue;

      if (item.variant_id !== wanted) {
        fixes[item.key] = 0;
      } else if (keptWanted) {
        fixes[item.key] = 0;
      } else {
        keptWanted = true;
        if (item.quantity !== 1) fixes[item.key] = 1;
      }
    }

    return { updates: fixes, holdsWanted: keptWanted };
  }

  // --- writes -----------------------------------------------------------

  function post(path, body) {
    return passthrough(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
  }

  function getCart() {
    return passthrough('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(remember);
  }

  /* Ask the theme to re-render its cart section from the server.

     A coupon line is added, removed or swapped after Shopify has already
     rendered the markup the theme is about to paint, so that markup shows the
     cart as it was a moment ago. The theme re-renders from the server when it
     sees `cart:update` carrying no `sections` payload, so announcing the change
     is the whole repaint.

     Announced unconditionally whenever this file changed a coupon, rather than
     when the rendered rows look wrong: the row set is only one of the things
     that can be stale, and a comparison that misses leaves the shopper looking
     at a coupon the cart does not have.

     `detail.data` must exist — the theme's handler reads
     `detail.data.sections?.[id]` unguarded, and a bare CustomEvent has a null
     detail. */
  function announce(cart) {
    if (!cart) return;
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: { resource: cart, sourceId: 'frido-free-gift', data: {} }
      })
    );
  }

  /* An attribute write is itself a cart mutation, so it can re-enter this file
     through the listeners below. Shopify silently discards some keys (anything
     prefixed `__`), which means a write can never satisfy its own read-back —
     and without this guard that is an endless request loop. Refusing to repeat
     a payload bounds it at one wasted write. */
  var lastWritten = null;

  /* Written last, so it never delays the line the shopper sees. */
  function writeAttributes(cart) {
    var current = cart.attributes || {};
    if (!cart.token || current[TOKEN_ATTR] === cart.token) return;

    var payload = TOKEN_ATTR + '=' + cart.token;
    if (payload === lastWritten) {
      console.warn('[free gift] cart attribute did not persist; not retrying');
      return;
    }
    lastWritten = payload;

    var attributes = {};
    attributes[TOKEN_ATTR] = cart.token;
    return post('/cart/update.js', { attributes: attributes }).catch(noop);
  }

  // --- reconciliation ---------------------------------------------------

  /* `cart` may be authoritative or predicted. `verify` re-runs the pass against
     a freshly read cart afterwards, which is what makes acting on a prediction
     safe. */
  function pass(cart, verify) {
    if (running) {
      /* A cart change landed mid-pass; this pass may already be out of date. */
      again = true;
      return;
    }
    if (!cart || !cart.items) return schedule();
    running = true;

    var wanted = couponFor(cart);
    var fixed = lineFixes(cart, wanted);
    var updates = fixed.updates;
    var changed = Object.keys(updates).length > 0;
    /* A shopper-owned line of the same variant counts as present — we do not add
       a second one on top of it, and we never remove it. */
    var add = wanted && !fixed.holdsWanted && !holdsAny(cart, wanted);

    var work = Promise.resolve();

    if (changed) {
      work = work.then(function () { return post('/cart/update.js', { updates: updates }); });
    }

    if (add) {
      work = work.then(function () {
        var properties = {};
        properties[AUTO_ADDED] = 'true';
        properties[LINE_TYPE] = 'free-gift';
        return post('/cart/add.js', {
          items: [{ id: wanted, quantity: 1, properties: properties }]
        });
      });
    }

    var touched = changed || add;

    work
      .then(function () {
        /* Read back rather than predict the result: an add can be rejected for
           stock, and the attributes must describe the cart that exists. */
        if (!touched) return writeAttributes(cart);
        return getCart().then(function (settled) {
          if (!settled) return;
          announce(settled);
          return writeAttributes(settled);
        });
      })
      .catch(noop)
      .then(function () {
        running = false;
        if (again) {
          again = false;
          schedule();
        } else if (verify && touched) {
          /* The pass acted on a guess. Confirm it against the real cart. */
          schedule();
        }
      });
  }

  function reconcile() {
    if (running) {
      again = true;
      return;
    }
    getCart().then(function (cart) { pass(cart, false); }).catch(noop);
  }

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(reconcile, SETTLE_MS);
  }

  // --- one-render reconcile ---------------------------------------------

  /* A line change made from the cart drawer asks Shopify to render the cart
     sections as part of the same request, and the drawer paints from that
     payload. Those sections describe the cart the change produced — which still
     holds the coupon this file is about to drop. Anything this file does next is
     therefore racing a repaint that does not know about it:

       act during it → the repaint lands after our mutation and restores the row
                       the shopper just removed; they have to remove it twice
       act after it  → the line goes, then the coupon goes in a second repaint,
                       which reads as jitter

     Both were shipped and both were wrong, so this path removes the race rather
     than moving it. It holds the drawer's response back, applies the coupon
     change, and answers with sections rendered AFTER that change. One repaint,
     showing both.

     The extra round trip is not visible: the drawer removes the row optimistically
     the moment it is clicked, so all the wait extends is the lock on the
     line-item controls.

     Taken only when the request asked for sections — i.e. when it drives a
     repaint. Every other cart mutation keeps the fire-and-forget path below, so
     nothing else ever waits on this file. */

  /* The `sections` a mutation asked Shopify to render, or null if it asked for
     none. Read from the outgoing request rather than the response so the
     decision to hold is made before anything is awaited. */
  function sectionsRequest(init) {
    if (!init || typeof init.body !== 'string') return null;
    var body;
    try {
      body = JSON.parse(init.body);
    } catch (e) {
      return null;
    }
    if (!body || !body.sections) return null;
    return { sections: body.sections, sections_url: body.sections_url };
  }

  /* What the coupon rule wants done to `cart`, or null if it wants nothing —
     which is the common case and the one that costs nothing. */
  function planFor(cart) {
    var wanted = couponFor(cart);
    var fixed = lineFixes(cart, wanted);
    var updates = fixed.updates;
    var changed = Object.keys(updates).length > 0;
    var add = wanted && !fixed.holdsWanted && !holdsAny(cart, wanted);
    if (!changed && !add) return null;
    return { updates: updates, changed: changed, add: add, wanted: wanted };
  }

  function postJson(path, body) {
    return post(path, body).then(function (res) { return res.ok ? res.json() : null; });
  }

  /* Applies `plan` and resolves with the settled cart carrying freshly rendered
     `sections`. */
  function applyPlan(plan, want) {
    if (plan.changed && !plan.add) {
      /* The case the shopper hits: a removal dropped the subtotal below a
         threshold. One request carries both the coupon drop and the render. */
      return postJson('/cart/update.js', {
        updates: plan.updates,
        sections: want.sections,
        sections_url: want.sections_url
      });
    }

    var work = Promise.resolve();
    if (plan.changed) {
      work = work.then(function () { return post('/cart/update.js', { updates: plan.updates }); });
    }
    if (plan.add) {
      work = work.then(function () {
        var properties = {};
        properties[AUTO_ADDED] = 'true';
        properties[LINE_TYPE] = 'free-gift';
        return post('/cart/add.js', {
          items: [{ id: plan.wanted, quantity: 1, properties: properties }]
        });
      });
    }
    /* `/cart/add.js` answers with the lines it added, not the whole cart, so the
       render is asked for separately once the coupon is in place. */
    return work.then(function () {
      return postJson('/cart/update.js', {
        sections: want.sections,
        sections_url: want.sections_url
      });
    });
  }

  /* Hands the caller `cart` in place of the body Shopify returned. Only the
     JSON is stood in for; the caller reads nothing else off the response. */
  function replaceBody(res, cart) {
    return new Response(JSON.stringify(cart), {
      status: res.status,
      statusText: res.statusText,
      headers: { 'Content-Type': 'application/json' }
    });
  }

  function withTimeout(promise, ms) {
    return new Promise(function (resolve, reject) {
      var settled = false;
      var t = setTimeout(function () {
        if (settled) return;
        settled = true;
        reject(new Error('timeout'));
      }, ms);
      promise.then(
        function (value) {
          if (settled) return;
          settled = true;
          clearTimeout(t);
          resolve(value);
        },
        function (error) {
          if (settled) return;
          settled = true;
          clearTimeout(t);
          reject(error);
        }
      );
    });
  }

  function holdForReconcile(res, want) {
    return res
      .clone()
      .json()
      .then(function (body) {
        if (!body || !body.items) {
          /* Not a cart we can read. Fall back to the ordinary path. */
          schedule();
          return res;
        }
        remember(body);

        /* A reconcile already in flight owns the coupon. Tell it the cart moved
           under it — exactly what a re-entrant pass does — and let it drive its
           own repaint rather than starting a second one behind it. */
        if (running) {
          again = true;
          return res;
        }

        var plan = planFor(body);
        if (!plan) {
          /* Nothing to reconcile, so Shopify's own sections are already right.
             Still owed the cart-token attribute the ordinary pass would write. */
          writeAttributes(body);
          return res;
        }

        running = true;
        var settled = null;
        var failed = false;

        return withTimeout(applyPlan(plan, want), HOLD_MS)
          .then(function (cart) {
            if (cart && cart.items) settled = cart;
            else failed = true;
          })
          .catch(function () { failed = true; })
          .then(function () {
            running = false;
            if (settled) {
              remember(settled);
              /* Written after the response is composed, so it never delays the
                 repaint. Item counts are unaffected, so the sections stay valid. */
              writeAttributes(settled);
            }
            /* Timed out or half-applied — let the ordinary pass put the cart
               right, and repaint from that instead. */
            if (failed || again) {
              again = false;
              schedule();
            }
            return settled ? replaceBody(res, settled) : res;
          });
      })
      .catch(function () {
        /* The body could not be read, so nothing above ran. Reconcile the
           ordinary way instead of leaving the coupon unexamined. */
        schedule();
        return res;
      });
  }

  // --- triggers ---------------------------------------------------------

  /* Merge a cart/add.js response into the last known cart. Shopify returns the
     resulting line, not a delta, so matching on `key` both replaces a line whose
     quantity grew and appends one that is new. */
  function predictFrom(added) {
    if (!lastCart || !added || !added.items) return null;

    var items = lastCart.items.slice();
    for (var i = 0; i < added.items.length; i++) {
      var line = added.items[i];
      var replaced = false;
      for (var j = 0; j < items.length; j++) {
        if (items[j].key === line.key) {
          items[j] = line;
          replaced = true;
          break;
        }
      }
      if (!replaced) items.push(line);
    }

    return { token: lastCart.token, attributes: lastCart.attributes, items: items };
  }

  /* Reacting to the mutation response directly is what keeps the coupon close
     behind the shopper's action: waiting for the SDK's `$spectrum:cart:updated`
     costs that SDK its own /cart.js read first, and the debounce after it. */
  function onMutation(url, body) {
    if (CART_ADD.test(url)) {
      var predicted = predictFrom(body);
      if (predicted) return pass(predicted, true);
      /* Nothing to predict from — fall back to reading the cart. */
      return schedule();
    }

    /* update / change / clear all answer with the whole cart. */
    if (body && body.items) return pass(remember(body), false);
    return schedule();
  }

  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var result = passthrough.apply(window, arguments);
    if (!CART_MUTATION.test(url)) return result;

    /* Only the cart drawer and the cart page ask for `sections`, and only they
       repaint from the answer. Hold those; leave every other mutation on the
       fire-and-forget path so nothing else ever waits on this file. */
    var want = CART_LINE_CHANGE.test(url) ? sectionsRequest(init) : null;
    if (want) {
      return result.then(function (res) {
        return res && res.ok ? holdForReconcile(res, want) : res;
      });
    }

    result
      .then(function (res) {
        if (!res || !res.ok) return;
        return res.clone().json().then(function (body) { onMutation(url, body); });
      })
      .catch(noop);

    return result;
  };

  /* Fallbacks for everything the wrapper cannot see: mutations made before this
     file loaded, the theme's own repaints, and a cart changed in another tab. */
  document.addEventListener('$spectrum:cart:updated', schedule);
  document.addEventListener('cart:update', schedule);

  /* A cart can arrive at a page already holding the wrong coupon. */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reconcile);
  } else {
    reconcile();
  }
})();
